import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getMobileBottomTabs, isMoreActive, isTabActive } from './mobileNavConfig';
import {
  MOBILE_BOTTOM_NAV_HEIGHT,
  MOBILE_TAB_BAR_HEIGHT,
  MOBILE_TAB_BAR_MARGIN,
  liquidGlassSx,
} from './glassStyles';

MobileBottomNav.propTypes = {
  onOpenMore: PropTypes.func.isRequired,
};

export default function MobileBottomNav({ onOpenMore }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tabs = getMobileBottomTabs();
  const moreActive = isMoreActive(tabs, pathname);

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: { xs: 'block', lg: 'none' },
        px: 1.25,
        pb: `calc(${MOBILE_TAB_BAR_MARGIN}px + env(safe-area-inset-bottom))`,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={(theme) => ({
          ...liquidGlassSx(theme, { radius: 22, border: 'all' }),
          height: MOBILE_TAB_BAR_HEIGHT,
          pointerEvents: 'auto',
          overflow: 'hidden',
        })}
      >
        <Stack direction="row" alignItems="stretch" justifyContent="space-around" sx={{ height: '100%', px: 0.5 }}>
          {tabs.map((tab) => {
            const active = tab.key === 'more' ? moreActive : isTabActive(tab, pathname);

            return (
              <Box
                key={tab.key}
                component="button"
                type="button"
                onClick={() => {
                  if (tab.key === 'more') {
                    onOpenMore();
                    return;
                  }
                  navigate(tab.path);
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.2,
                  color: active ? 'primary.main' : 'text.secondary',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'color 140ms ease',
                  '&:active': {
                    transform: 'scale(0.96)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 42,
                    height: 28,
                    borderRadius: 2,
                    bgcolor: active ? (theme) => alpha(theme.palette.primary.main, 0.16) : 'transparent',
                    boxShadow: active
                      ? (theme) => `inset 0 1px 0 ${alpha('#fff', 0.55)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.18)}`
                      : 'none',
                  }}
                >
                  {tab.icon}
                </Box>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontSize: 10.5,
                    fontWeight: active ? 700 : 500,
                    lineHeight: 1.15,
                    maxWidth: '100%',
                    px: 0.25,
                    letterSpacing: 0.1,
                  }}
                >
                  {tab.label}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

export { MOBILE_BOTTOM_NAV_HEIGHT };
